import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BG_CARD,
  BORDER_LIGHT,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

interface Advertiser {
  id: string;
  title: string;
  authorName: string;
  adSize: string | null;
  attachmentUrl: string | null;
  status: string;
  createdAt: string;
  spaceRevenue?: { amount: number; paymentMethod: string } | null;
}

interface EditionInfo {
  id: string;
  name: string;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/** Resolve an attachmentUrl (e.g. "uploads/file.png") to a full URL served by the API */
function resolveFileUrl(attachmentUrl: string): string {
  return `${API_BASE}/weekend-booklet/${attachmentUrl}`;
}

/**
 * BookletAdvertisersPage — Lists ad space purchasers from the booklet Advertisers category.
 */
export function BookletAdvertisersPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    async function fetch() {
      try {
        const ed = await apiClient<EditionInfo>('/weekend-booklet/editions/active');
        if (!ed?.id) { setLoading(false); return; }
        const ads = await apiClient<Advertiser[]>(`/weekend-booklet/submissions?editionId=${ed.id}&sectionCategory=Advertisers`);
        setAdvertisers(ads);

        // Pre-fetch images as blobs (authenticated)
        const urlMap: Record<string, string> = {};
        await Promise.all(
          ads.filter((a) => a.attachmentUrl).map(async (a) => {
            try {
              const resp = await window.fetch(resolveFileUrl(a.attachmentUrl!), {
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
              });
              if (resp.ok) {
                const blob = await resp.blob();
                urlMap[a.id] = URL.createObjectURL(blob);
              }
            } catch {}
          }),
        );
        setBlobUrls(urlMap);
      } catch {
        setError('Unable to load ad space purchasers.');
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [accessToken]);

  if (loading) {
    return <div style={styles.page}><p style={{ color: TEXT_MUTED, textAlign: 'center', padding: '2rem' }}>Loading advertisers…</p></div>;
  }

  return (
    <div style={styles.page}>
      <button style={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
      <h1 style={styles.title}>📣 Ad Space Purchasers</h1>
      <p style={styles.subtitle}>{advertisers.length} advertiser{advertisers.length !== 1 ? 's' : ''}</p>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {!error && advertisers.length === 0 && (
        <div style={styles.empty}>No ad space purchasers in the current booklet edition yet. Check back as the booklet is being prepared.</div>
      )}

      <div style={styles.list}>
        {advertisers.map((ad) => (
          <div key={ad.id} style={styles.card}>
            {blobUrls[ad.id] && (
              <button
                style={styles.thumbnailBtn}
                onClick={() => setLightboxUrl(blobUrls[ad.id])}
                aria-label={`View ad image for ${ad.title}`}
              >
                <img
                  src={blobUrls[ad.id]}
                  alt={`Ad: ${ad.title}`}
                  style={styles.thumbnail}
                />
                <span style={styles.viewLabel}>Tap to view</span>
              </button>
            )}
            <h3 style={styles.cardTitle}>{ad.title}</h3>
            <p style={styles.cardMeta}>Contact: {ad.authorName}</p>
            {ad.adSize && <span style={styles.badge}>{ad.adSize.replace(/([A-Z])/g, ' $1').trim()}</span>}
            {ad.spaceRevenue && (
              <p style={styles.cardMeta}>
                💰 ${Number(ad.spaceRevenue.amount).toFixed(2)} ({ad.spaceRevenue.paymentMethod})
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox popup */}
      {lightboxUrl && (
        <div style={styles.overlay} onClick={() => setLightboxUrl(null)}>
          <div style={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
            <button
              style={styles.closeBtn}
              onClick={() => setLightboxUrl(null)}
              aria-label="Close image"
            >
              ✕
            </button>
            <img
              src={lightboxUrl}
              alt="Ad image full view"
              style={styles.lightboxImg}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '1.5rem', minHeight: '100%', maxWidth: '600px', margin: '0 auto' },
  title: { fontFamily: FONT_HEADING, fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, margin: '0.5rem 0 0.25rem' },
  subtitle: { color: TEXT_MUTED, fontSize: '0.85rem', marginBottom: '1rem' },
  backBtn: { background: 'none', border: 'none', color: PRIMARY, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', padding: '4px 0', marginBottom: '0.5rem', display: 'block' },
  errorBanner: { backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem' },
  empty: { textAlign: 'center' as const, padding: '2rem 1rem', color: TEXT_MUTED, fontSize: '0.9rem' },
  list: { display: 'flex', flexDirection: 'column' as const, gap: '12px' },
  card: { backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px', padding: '1rem', display: 'flex', flexDirection: 'column' as const, gap: '6px' },
  cardTitle: { fontFamily: FONT_HEADING, fontSize: '1rem', fontWeight: 600, color: TEXT_DARK, margin: 0 },
  cardMeta: { fontSize: '0.8rem', color: TEXT_MUTED, margin: 0 },
  badge: { alignSelf: 'flex-start' as const, backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '0.65rem', fontWeight: 600, padding: '3px 8px', borderRadius: '12px', marginTop: '4px' },
  thumbnailBtn: { background: 'none', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '4px', cursor: 'pointer', alignSelf: 'flex-start' as const, position: 'relative' as const, overflow: 'hidden' as const },
  thumbnail: { width: '120px', height: '80px', objectFit: 'cover' as const, borderRadius: '6px', display: 'block' },
  viewLabel: { position: 'absolute' as const, bottom: '6px', left: '50%', transform: 'translateX(-50%)', fontSize: '0.6rem', backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 6px', borderRadius: '3px' },
  overlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  lightboxContent: { position: 'relative' as const, maxWidth: '90vw', maxHeight: '90vh' },
  lightboxImg: { maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' as const, borderRadius: '8px' },
  closeBtn: { position: 'absolute' as const, top: '-12px', right: '-12px', background: '#fff', border: 'none', borderRadius: '50%', width: '32px', height: '32px', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' },
};
